import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { User, GameMode, GameState, PuzzleInfo } from '../types';

interface AppState {
    // User state
    user: User | null;
    setUser: (user: User | null) => void;

    // Current game state
    currentMode: GameMode;
    setCurrentMode: (mode: GameMode) => void;

    currentPuzzleId: string | null;
    setCurrentPuzzleId: (id: string | null) => void;

    gameState: GameState | null;
    setGameState: (state: GameState | null | ((prev: GameState | null) => GameState | null)) => void;

    // Puzzle list
    puzzles: PuzzleInfo[];
    setPuzzles: (puzzles: PuzzleInfo[]) => void;

    // UI state
    isMuted: boolean;
    toggleMute: () => void;

    // Volume controls (0.0 to 1.0)
    musicVolume: number;
    sfxVolume: number;
    setMusicVolume: (volume: number) => void;
    setSfxVolume: (volume: number) => void;

    // Music player preferences
    excludedTracks: string[];
    setExcludedTracks: (tracks: string[]) => void;
    shuffleEnabled: boolean;
    toggleShuffle: () => void;

    // Music playback runtime state (not persisted — updated by audio service)
    nowPlayingTrack: string;
    musicIsActive: boolean;
    musicIsPaused: boolean;

    isDarkMode: boolean;
    toggleDarkMode: () => void;

    isKeyboardVisible: boolean;
    toggleKeyboard: () => void;



    // Global login modal trigger (any component can request it)
    loginModalOpen: boolean;
    setLoginModalOpen: (open: boolean) => void;

    // Session state
    sessionId: string | null;
    setSessionId: (id: string | null) => void;

    isCollab: boolean;
    setIsCollab: (isCollab: boolean) => void;

    // State version for optimistic updates
    stateVersion: number;
    incrementStateVersion: () => void;
}

export const useAppStore = create<AppState>()(
    devtools(
        persist(
            (set) => ({
                // User state
                user: null,
                setUser: (user) => set({ user }),

                // Current game state
                currentMode: 'classic',
                setCurrentMode: (mode) => set({ currentMode: mode }),

                currentPuzzleId: null,
                setCurrentPuzzleId: (id) => set({ currentPuzzleId: id }),

                gameState: null,
                setGameState: (stateOrUpdater) => set((current) => {
                    if (typeof stateOrUpdater === 'function') {
                        const newState = stateOrUpdater(current.gameState);
                        console.log('[appStore] setGameState (functional):',
                            newState ? `guesses: ${newState.guesses?.length}` : 'null');
                        return { gameState: newState };
                    }
                    console.log('[appStore] setGameState (direct):',
                        stateOrUpdater ? `guesses: ${stateOrUpdater.guesses?.length}` : 'null');
                    return { gameState: stateOrUpdater };
                }),

                // Puzzle list
                puzzles: [],
                setPuzzles: (puzzles) => set({ puzzles }),

                // UI state - these persist to localStorage
                isMuted: false,
                toggleMute: () => set((state) => ({ isMuted: !state.isMuted })),

                // Volume controls (0.0 to 1.0)
                musicVolume: 0,  // Default to 0% - users must explicitly enable music
                sfxVolume: 0.5,
                setMusicVolume: (volume) => set({ musicVolume: Math.max(0, Math.min(1, volume)) }),
                setSfxVolume: (volume) => set({ sfxVolume: Math.max(0, Math.min(1, volume)) }),

                // Music player preferences
                excludedTracks: [],
                setExcludedTracks: (tracks) => set({ excludedTracks: tracks }),
                shuffleEnabled: true,  // Default to shuffle on
                toggleShuffle: () => set((state) => ({ shuffleEnabled: !state.shuffleEnabled })),

                // Music playback runtime state (never persisted)
                nowPlayingTrack: '',
                musicIsActive: false,
                musicIsPaused: false,

                isDarkMode: false,
                toggleDarkMode: () => set((state) => ({ isDarkMode: !state.isDarkMode })),

                isKeyboardVisible: false,
                toggleKeyboard: () => set((state) => ({ isKeyboardVisible: !state.isKeyboardVisible })),



                // Global login modal trigger
                loginModalOpen: false,
                setLoginModalOpen: (open) => set({ loginModalOpen: open }),

                // Session state
                sessionId: null,
                setSessionId: (id) => set({ sessionId: id }),

                isCollab: false,
                setIsCollab: (isCollab) => set({ isCollab }),

                // State version
                stateVersion: 0,
                incrementStateVersion: () => set((state) => ({ stateVersion: state.stateVersion + 1 })),
            }),
            {
                name: 'hotorcold-storage',
                // Persist UI preferences AND navigation state for Android lifecycle resilience
                partialize: (state) => ({
                    isMuted: state.isMuted,
                    musicVolume: state.musicVolume,
                    sfxVolume: state.sfxVolume,
                    excludedTracks: state.excludedTracks,
                    shuffleEnabled: state.shuffleEnabled,
                    isKeyboardVisible: state.isKeyboardVisible,

                    isDarkMode: state.isDarkMode,
                    // Persist navigation state so Android doesn't reset on WebView reload
                    currentMode: state.currentMode,
                    currentPuzzleId: state.currentPuzzleId,
                }),
            }
        ),
        { name: 'HotOrColdStore' }
    )
);

// Selector hooks for common access patterns
export const useUser = () => useAppStore((state) => state.user);
export const useCurrentMode = () => useAppStore((state) => state.currentMode);
export const useGameState = () => useAppStore((state) => state.gameState);
export const useIsMuted = () => useAppStore((state) => state.isMuted);
